/**
 * Print Activations chapter — Canva page-04 Signals & Activations.
 * Gold Chinese luopan in the header band, Four Transformations 2×2,
 * compact week teaser, dark Month Trap.
 */

import React from "react";
import type {
  ActivationCard,
  MonthlyConsultationBundle,
  SiHuaKind,
  StemSiHuaActivation,
} from "../../../../utils/forecast/monthlyConsultation";
import {
  SI_HUA_LABEL,
  SI_HUA_PLAIN,
  activationLandingEnglish,
  starToEnglish,
} from "../../../../utils/forecast/monthlyConsultation/englishLabels";
import { kindFromActivationTitle } from "../../shared/SiHuaChip";
import {
  PdfChapterShell,
  PdfCornerLuopan,
  PdfSiHuaCard,
  PdfWeekTile,
} from "../atoms";
import type { PdfSiHuaRole } from "../atoms";
import {
  activationsIntroLine,
  clipPdfText,
  monthTrapBody,
  siHuaActionLine,
  siHuaKindFromRole,
  siHuaStarHeading,
  SI_HUA_PRINT_ROLES,
  weekStanceFromQuality,
  weekTeaserLabel,
} from "../pdfCopy";
import type { SiHuaPrintRole, WeekTeaserStance } from "../pdfCopy";
import {
  bodyFontFamily,
  charcoal,
  displayFontFamily,
  gold,
  green,
  insightDark,
  mutedGray,
  terracotta,
} from "../pdfTheme";

/**
 * Props for the PDF activations chapter.
 */
export interface PdfChapterActivationsProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/** Empty-state body when a transformation is not landing this month. */
const EMPTY_ROLE_BODY =
  "This transformation is not landing in the month chart.";

/**
 * Resolve the print role union onto the atom prop type (identical string union).
 *
 * @param role - Copy-helper print role
 * @returns PdfSiHuaCard role
 */
function toPdfRole(role: SiHuaPrintRole): PdfSiHuaRole {
  return role;
}

/**
 * Find the first stem activation matching a Si Hua kind.
 *
 * @param activations - Live stem activations
 * @param kind - Target kind
 * @returns Matching activation, or null
 */
function activationForKind(
  activations: readonly StemSiHuaActivation[],
  kind: SiHuaKind
): StemSiHuaActivation | null {
  const match = activations.find((row) => row.kind === kind);
  return match !== undefined ? match : null;
}

/**
 * Find the first activation card whose title maps to a Si Hua kind.
 *
 * @param cards - Ranked activation cards
 * @param kind - Target kind
 * @returns Matching card, or null
 */
function cardForKind(
  cards: readonly ActivationCard[],
  kind: SiHuaKind
): ActivationCard | null {
  const match = cards.find((card) => kindFromActivationTitle(card.title) === kind);
  return match !== undefined ? match : null;
}

/**
 * Theme color for a week-teaser stance pill.
 *
 * @param stance - Push / Careful / Steady / Rest
 * @returns Theme hex
 */
function stanceColor(stance: WeekTeaserStance): string {
  if (stance === "Push") {
    return green;
  }
  if (stance === "Careful") {
    return gold;
  }
  return mutedGray;
}

/**
 * Signals & Activations — one A4 sheet of Four Transformations + trap.
 */
export const PdfChapterActivations: React.FC<PdfChapterActivationsProps> = ({
  bundle,
  pageNumber,
}) => {
  const activations = Array.isArray(bundle.stemActivations)
    ? bundle.stemActivations
    : [];
  const cards = Array.isArray(bundle.activationCards)
    ? bundle.activationCards
    : [];
  const weeks = Array.isArray(bundle.weekPlan) ? bundle.weekPlan.slice(0, 4) : [];

  /** Canva page-04 static intro (briefing fields intentionally ignored). */
  const intro = activationsIntroLine("", "");

  const trapName =
    typeof bundle.failureMode.name === "string"
      ? bundle.failureMode.name.trim()
      : "";
  const trapBody = clipPdfText(
    monthTrapBody(
      typeof bundle.failureMode.description === "string"
        ? bundle.failureMode.description
        : "",
      typeof bundle.failureMode.exitMove === "string"
        ? bundle.failureMode.exitMove
        : ""
    ),
    220
  );

  return (
    <PdfChapterShell
      chapterId="activations"
      eyebrow=""
      title=""
      pageNumber={pageNumber}
      hideHeader={true}
      compact
    >
      {/*
        Grow into leftover A4 height: content stacks; Month Trap pins above
        the footer. Si Hua cards stay content-sized (Canva density).
      */}
      <div
        className="mc-pdf-activations-fill"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          flex: "1 1 auto",
          minHeight: 0,
          height: "100%",
          fontFamily: bodyFontFamily,
        }}
      >
        <div
          className="mc-pdf-activations-hero mc-pdf-corner-luopan-band"
          style={{ flexShrink: 0 }}
        >
          <PdfCornerLuopan />
          <p
            className="mc-pdf-eyebrow"
            style={{
              margin: 0,
              position: "relative",
              zIndex: 1,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: terracotta,
            }}
          >
            Four transformations
          </p>
          <h2
            style={{
              margin: "4px 0 8px",
              position: "relative",
              zIndex: 1,
              fontFamily: displayFontFamily,
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              color: charcoal,
            }}
          >
            Signals & Activations
          </h2>
          {intro.length > 0 ? (
            <p
              style={{
                margin: 0,
                position: "relative",
                zIndex: 1,
                fontSize: 12,
                lineHeight: 1.45,
                color: mutedGray,
              }}
            >
              {clipPdfText(intro, 160)}
            </p>
          ) : null}
        </div>

        <div
          className="mc-pdf-activations-sihua-block"
          style={{ flexShrink: 0 }}
        >
          <p
            className="mc-pdf-section-rule"
            style={{
              margin: "2px 0 10px",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: charcoal,
            }}
          >
            Lu · Quan · Ke · Ji
          </p>
          <div className="mc-pdf-grid-2x2 mc-pdf-activations-sihua">
            {SI_HUA_PRINT_ROLES.map((role) => {
              const kind = siHuaKindFromRole(role);
              const activation = activationForKind(activations, kind);
              const card = cardForKind(cards, kind);
              const pdfRole = toPdfRole(role);

              if (activation === null) {
                return (
                  <PdfSiHuaCard
                    key={role}
                    role={pdfRole}
                    palace=""
                    starName="Not active this month"
                    starChinese=""
                    body={EMPTY_ROLE_BODY}
                    action=""
                  />
                );
              }

              const starEnglish = starToEnglish(activation.starName);
              const kindLabel = SI_HUA_LABEL[kind];
              const palace = activationLandingEnglish(activation);
              const meaningRaw =
                card !== null && typeof card.meaning === "string"
                  ? card.meaning.trim()
                  : SI_HUA_PLAIN[kind];
              const moveRaw =
                card !== null && typeof card.move === "string" ? card.move : "";

              return (
                <PdfSiHuaCard
                  key={role}
                  role={pdfRole}
                  palace={palace}
                  starName={siHuaStarHeading(starEnglish, kindLabel)}
                  starChinese=""
                  body={clipPdfText(meaningRaw, 160)}
                  action={siHuaActionLine(clipPdfText(moveRaw, 90))}
                />
              );
            })}
          </div>
        </div>

        {weeks.length > 0 ? (
          <div
            className="mc-pdf-activations-weeks-block"
            style={{
              flex: "0 1 auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <p
              className="mc-pdf-section-rule"
              style={{
                margin: "4px 0 10px",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: charcoal,
                flexShrink: 0,
              }}
            >
              Week-by-week action plan
            </p>
            <div className="mc-pdf-grid-2x2 mc-pdf-activations-weeks">
              {weeks.map((row, index) => {
                const quality =
                  typeof row.quality === "string" ? row.quality : "";
                const stance = weekStanceFromQuality(quality);
                const windowLabel =
                  typeof row.window === "string" ? row.window : "";
                const useFor = typeof row.useFor === "string" ? row.useFor : "";
                return (
                  <PdfWeekTile
                    key={`${weekTeaserLabel(windowLabel)}-${String(index)}`}
                    week={weekTeaserLabel(windowLabel)}
                    stance={stance}
                    stanceColor={stanceColor(stance)}
                    body={clipPdfText(useFor, 90)}
                  />
                );
              })}
            </div>
          </div>
        ) : null}

        {trapName.length > 0 || trapBody.length > 0 ? (
          <aside
            className="mc-pdf-dark-callout mc-pdf-trap-banner"
            style={{
              background: insightDark,
              borderRadius: 14,
              padding: "12px 14px",
              boxSizing: "border-box",
              width: "100%",
              fontFamily: bodyFontFamily,
              flexShrink: 0,
              marginTop: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 12,
                marginBottom: trapBody.length > 0 ? 4 : 0,
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: gold,
                }}
              >
                The Month Trap
              </p>
              {trapName.length > 0 ? (
                <p
                  style={{
                    margin: 0,
                    fontFamily: displayFontFamily,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: terracotta,
                    textAlign: "right",
                  }}
                >
                  {trapName}
                </p>
              ) : null}
            </div>
            {trapBody.length > 0 ? (
              <p
                style={{
                  margin: 0,
                  fontSize: 11,
                  lineHeight: 1.45,
                  color: "#f5f0e8",
                }}
              >
                {trapBody}
              </p>
            ) : null}
          </aside>
        ) : null}
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterActivations;
